import React from 'react'
import { Dialog, IconButton, Divider, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { createStyles, withStyles, WithStyles } from '@mui/styles'
import CloseIcon from '@mui/icons-material/Close'
import clsx from 'clsx'
import { Colors } from '../../../theme'
import { GreenButton } from '../Buttons/GreenButton'
import { OutlinedButton, propTypes as OutlinedButtonProps } from '../Buttons/OutlinedButton'
import { Loader } from '../../Loader'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      zIndex: 100,
      margin: 0,
      padding: theme.spacing(3),
      paddingTop: theme.spacing(5),
      marginBottom: theme.spacing(1),
    },
    dialogTitle: {
      lineHeight: 0,
      fontWeight: 400,
      fontSize: 26,
      opacity: 0.87,
      color: Colors.white,
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: '#afb0b2',
    },
    dialog: {
      background: Colors.darkGrey,
      maxWidth: '100%',
      width: (props: IStyles) => props.width || 500,
      borderRadius: 10,
    },
    dialogWidthAuto: {
      width: 'auto',
      maxHeight: '100% !important',
    },
    divider: {
      width: '100%',
      marginBottom: theme.spacing(3),
      background: '#E9ECEF',
      opacity: 0.38,
    },
    wrapBtn: {
      display: 'flex',
      justifyContent: 'flex-end',
      width: '100%',
      padding: '8px 24px',
    },
  })

interface IStyles {
  width?: number
  isWidthAuto?: boolean
}

interface IModal extends WithStyles<typeof styles> {
  isOpen: boolean
  isWidthAuto?: boolean
  modalClose: () => void
  onSubmit?: () => void
  title: string
  submitTitle?: string
  cancelTitle?: string
  submitButtonProps?: OutlinedButtonProps
  cancelButtonProps?: OutlinedButtonProps
  children: React.ReactNode
  actions?: React.ReactNode
  isSubmit?: boolean
  isSubmitDisabled?: boolean
  isLoading?: boolean
  width?: number
}

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string
  children: React.ReactNode
  onClose: () => void
}

const MuiDialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props
  return (
    <DialogTitle className={classes.root} {...other}>
      <span className={classes.dialogTitle}>{children}</span>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  )
})

const MuiDialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    color: theme.palette.primary.light,
    opacity: 0.87,
  },
}))(DialogContent)

const MuiDialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: 0,
    paddingBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
}))(DialogActions)

export const Modal = withStyles(styles)((props: IModal) => {
  const {
    classes,
    modalClose,
    onSubmit,
    isOpen,
    title,
    submitTitle,
    cancelTitle,
    children,
    actions,
    isWidthAuto,
    isSubmit,
    isSubmitDisabled = false,
    isLoading,
  } = props

  return (
    <>
      <Dialog
        classes={{ paper: clsx(classes.dialog, isWidthAuto ? classes.dialogWidthAuto : undefined) }}
        onClose={modalClose}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        {isLoading && <Loader />}

        <MuiDialogTitle id="customized-dialog-title" onClose={modalClose}>
          {title}
        </MuiDialogTitle>

        <MuiDialogContent>{children}</MuiDialogContent>
        {!actions && (
          <MuiDialogActions>
            <Divider className={classes.divider} />
            <div className={classes.wrapBtn}>
              <OutlinedButton width={120} text={cancelTitle || 'Cancel'} type="button" onClick={modalClose} mr={15} />
              {isSubmit && (
                <GreenButton
                  disabled={isSubmitDisabled}
                  width={120}
                  text={submitTitle || 'Confirm'}
                  onClick={onSubmit}
                />
              )}
            </div>
          </MuiDialogActions>
        )}
      </Dialog>
    </>
  )
})
